/**
 * Rolling monthly rows up to quarters, half-years or years.
 *
 * Everything downstream — charts, tooltips, the table — consumes ONE row shape, so the
 * aggregate must produce exactly the shape the API produces per month. That is why the
 * output keeps the `month` key (as the period's key) rather than inventing a `period`
 * one: adding a second identity field would mean touching every chart to read it.
 *
 * How a field rolls up is a property of the field, not of the grain:
 *
 * - FLOWS sum. Revenue over a quarter is the three months added.
 * - STOCKS take the LAST month in the period. MRR, the cash balance and the receivable
 *   balance are closing positions — summing three closing balances would state cash
 *   three times over. Anything not listed as a flow is treated as a stock, so a balance
 *   account added to the API later rolls up correctly without a change here.
 * - MARGINS are recomputed from the summed parts, never averaged (jabr/CLAUDE.md rule
 *   5). The mean of three monthly margins is not the quarter's margin.
 * - NRR and GRR are measured on the COHORT that opened the period: the customers holding
 *   MRR the day the period began, and what those same customers hold on its last day.
 *   Neither averaging nor chain-linking the monthly ratios works — averaging is plainly
 *   wrong, and chaining shifts the denominator every month, since each month's base is
 *   the previous month's CLOSING MRR and already contains customers the numerator
 *   excludes. Nor can the monthly movement bridge be summed over a period: a customer who
 *   arrives in month 1 and leaves in month 2 books churn against an opening base it was
 *   never part of, which turns Pifa AB's 2025 Q2 into -532 % NRR. Only the cohort answers
 *   the question at every grain, which is why the API ships per-customer MRR.
 * - `operating_per_day` is the period's cost over the period's actual days, so a
 *   quarter containing February is not weighted as if every month had 31 days.
 *
 * The Pifa AB financial year is 1 Jan – 31 Dec, so quarters and halves are the calendar
 * ones and no fiscal offset enters anywhere.
 */

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/** Flows. Everything else on the row is a closing position and takes the last month. */
const SUM = [
	'revenue', 'direct', 'external', 'personnel', 'depreciation', 'other_operating',
	'financial', 'gross_profit', 'ebitda', 'operating_result', 'operating_cost', 'result',
	'new', 'expansion', 'contraction', 'churn', 'net_new',
];
const RETENTION = ['nrr', 'grr'];
/** Per-customer closing MRR: the cohort input, rolled up by cohort logic, not copied. */
const COHORT = ['mrr_customers'];
const MAPS = ['articles', 'accounts'];
/** Recomputed from the rolled-up figures, so never carried over from a month. */
const DERIVED = ['gross_margin', 'ebitda_margin', 'arpa', 'operating_per_day',
	'month', 'label', 'tick', 'ledger_partial', 'days', 'months_n', 'months_expected',
	'mrr_start'];
const SKIP = new Set([...SUM, ...RETENTION, ...COHORT, ...MAPS, ...DERIVED]);

export const GRAINS = {
	month: {label: 'Månad', size: 1, key: m => m, sv: 'månad', plural: 'månader'},
	quarter: {label: 'Kvartal', size: 3, sv: 'kvartal', plural: 'kvartal',
		key: m => `${m.slice(0, 4)}-Q${Math.floor((+m.slice(5, 7) - 1) / 3) + 1}`},
	half: {label: 'Halvår', size: 6, sv: 'halvår', plural: 'halvår',
		key: m => `${m.slice(0, 4)}-H${+m.slice(5, 7) <= 6 ? 1 : 2}`},
	year: {label: 'År', size: 12, sv: 'år', plural: 'år', key: m => m.slice(0, 4)},
};

/** Full label for tooltips and the table; short one for axis ticks. */
function labels(grain, key) {
	const year = key.slice(0, 4), yy = key.slice(2, 4);
	if (grain === 'month') return {label: `${MONTHS[+key.slice(5, 7) - 1]} ${year}`,
		tick: `${MONTHS[+key.slice(5, 7) - 1]} ${yy}`};
	if (grain === 'year') return {label: year, tick: year};
	const part = key.slice(5);                       // "Q3" or "H2"
	return {label: `${part} ${year}`, tick: `${part} ${yy}`};
}

const daysIn = m => new Date(+m.slice(0, 4), +m.slice(5, 7), 0).getDate();

/**
 * Cohort retention: what the customers who opened the period hold when it closes. The
 * mirror of retention() in api/src/services/metrics.js and engine/metrics.py — one
 * definition in three languages, so a figure here is the figure there.
 *
 * A credit note can push a customer's spread MRR negative for a month, so positions are
 * floored at zero on both sides rather than taken as given; the API ships only positions
 * above its MRR threshold, and this holds even if that ever changes.
 */
function retention(open, close) {
	let base = 0, kept = 0, gross = 0;
	for (const [c, v] of Object.entries(open)) {
		if (!(v > 0)) continue;
		const end = Math.max(close[c] ?? 0, 0);
		base += v;
		kept += end;
		// GRR counts no customer above what they opened with, so expansion cannot mask a
		// churn elsewhere — that is the whole difference between the two ratios.
		gross += Math.min(v, end);
	}
	return base > 0 ? {nrr: kept / base, grr: gross / base} : {nrr: null, grr: null};
}

/**
 * Window `all` to `period` ({from, to} inclusive, or null for everything) and group the
 * result into periods of `grain`. Windowing lives here rather than in the caller because
 * a period's opening cohort is the CLOSING position of the month before it — which is
 * outside the window for the first period, and so has to be looked up in the full series.
 *
 * `month` grain still passes through here, so a month row and a quarter row carry the
 * same added fields (`label`, `tick`, `days`) — one shape means no component below needs
 * to know which grain it is drawing.
 */
export function toPeriods(all, grain = 'month', period = null) {
	const g = GRAINS[grain] ?? GRAINS.month;
	const from = period ? all.findIndex(m => m.month >= period.from) : 0;
	const start = from < 0 ? all.length : from;
	const months = period ? all.slice(start).filter(m => m.month <= period.to) : all;

	const buckets = new Map();
	for (const m of months) {
		const key = g.key(m.month);
		if (!buckets.has(key)) buckets.set(key, []);
		buckets.get(key).push(m);
	}
	// The period opens on the last cohort seen BEFORE it, which may be several months
	// back: the MRR series has gaps — a bimonthly subscription is not spread into every
	// month, and January 2026 is empty between December and February. movement() in the
	// API skips those months rather than reading them as a total churn and a total
	// recovery, and this must agree with it or the same month reads differently in the
	// table than in the API. So a gap carries the last real position forward.
	const nonEmpty = r => Object.keys(r.mrr_customers ?? {}).length > 0;
	let opening = all.slice(0, start).reverse().find(nonEmpty)?.mrr_customers ?? null;

	return [...buckets.entries()].map(([key, rows]) => {
		const last = rows[rows.length - 1];
		const out = {month: key, ...labels(grain, key)};

		// Stocks first: start from the closing month, then overwrite the flows with sums.
		for (const k of Object.keys(last)) if (!SKIP.has(k)) out[k] = last[k];
		for (const k of SUM) {
			const vals = rows.map(r => r[k]).filter(v => v != null);
			out[k] = vals.length ? vals.reduce((a, b) => a + b, 0) : null;
		}
		// The period closes on its last month that actually carries a cohort; a period
		// whose every month is a gap has no closing position and so no retention.
		const closing = [...rows].reverse().find(nonEmpty)?.mrr_customers ?? null;
		const {nrr, grr} = opening && closing ? retention(opening, closing) : {nrr: null, grr: null};
		out.nrr = nrr;
		out.grr = grr;
		out.mrr_start = opening ? Object.values(opening).reduce((a, b) => a + b, 0) : null;
		out.mrr_customers = last.mrr_customers ?? {};
		if (closing) opening = closing;      // this period's close opens the next
		for (const k of MAPS) {
			const merged = {};
			for (const r of rows) {
				for (const [kk, v] of Object.entries(r[k] ?? {})) merged[kk] = (merged[kk] ?? 0) + (v ?? 0);
			}
			out[k] = merged;
		}

		out.gross_margin = out.revenue ? out.gross_profit / out.revenue : null;
		out.ebitda_margin = out.revenue ? out.ebitda / out.revenue : null;
		out.arpa = out.customers ? Math.round(out.mrr / out.customers) : null;
		out.days = rows.reduce((a, r) => a + daysIn(r.month), 0);
		out.operating_per_day = out.days ? Math.round(out.operating_cost / out.days) : null;

		// A period holding a partly-booked month is not an outcome, exactly as the month
		// is not (jabr/CLAUDE.md rule 4) — the label propagates rather than being averaged
		// away by the two complete months beside it.
		out.ledger_partial = rows.some(r => r.ledger_partial);
		// A period the chosen window only partly covers is still fully BOOKED, so it stays
		// in the footer's sums; it is marked separately so the reader knows Q3 is two
		// months here, not three.
		out.months_n = rows.length;
		out.months_expected = g.size;
		return out;
	});
}
